'use client';

import { cn } from '@zealot/ui/lib/utils';
import * as React from 'react';

// gloss
type FileUploadFile = {
  id: string;
  file: File;
  previewUrl?: string;
  progress?: number;
  error?: string;
  url?: string;
  key?: string;
};

type FileValidationError = {
  file: File;
  reason: 'size' | 'type' | 'count';
  message: string;
};

type FileUploadContextValue = {
  files: FileUploadFile[];
  onFilesChange: (files: FileUploadFile[]) => void;
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
  maxFiles?: number;
  disabled?: boolean;
  openFilePicker: () => void;
  onValidationError?: (errors: FileValidationError[]) => void;
};

type FileItemContextValue = {
  file: FileUploadFile;
};

const FileUploadContext = React.createContext<FileUploadContextValue | null>(null);

const useFileUploadContext = () => {
  const ctx = React.useContext(FileUploadContext);
  if (!ctx) throw new Error('FileUpload compound components must be used within <FileUpload>');
  return ctx;
};

const FileItemContext = React.createContext<FileItemContextValue | null>(null);

const useFileItemContext = () => {
  const ctx = React.useContext(FileItemContext);
  if (!ctx) throw new Error('FileUploadFileItem sub-components must be used within <FileUploadFileItem>');
  return ctx;
};

const validateFiles = (
  incoming: File[],
  existing: FileUploadFile[],
  { accept, maxSize, maxFiles, multiple }: Pick<FileUploadContextValue, 'accept' | 'maxSize' | 'maxFiles' | 'multiple'>,
): { valid: File[]; errors: FileValidationError[] } => {
  const errors: FileValidationError[] = [];
  const valid: File[] = [];

  const acceptedTypes = accept
    ?.split(',')
    .map((t) => t.trim())
    .filter(Boolean);

  const matchesAccept = (file: File) => {
    if (!acceptedTypes?.length) return true;
    return acceptedTypes.some((pattern) => {
      if (pattern.startsWith('.')) {
        return file.name.toLowerCase().endsWith(pattern.toLowerCase());
      }
      if (pattern.endsWith('/*')) {
        return file.type.startsWith(pattern.slice(0, -1));
      }
      return file.type === pattern;
    });
  };

  for (const file of incoming) {
    if (!matchesAccept(file)) {
      errors.push({ file, reason: 'type', message: `${file.name} is not an accepted file type` });
      continue;
    }
    if (maxSize && file.size > maxSize) {
      const sizeMB = (maxSize / 1_048_576).toFixed(1);
      errors.push({ file, reason: 'size', message: `${file.name} exceeds ${sizeMB} MB limit` });
      continue;
    }
    valid.push(file);
  }

  const totalAllowed = multiple ? (maxFiles ?? Infinity) : 1;
  const availableSlots = Math.max(0, totalAllowed - existing.length);

  if (valid.length > availableSlots) {
    const excess = valid.splice(availableSlots);
    for (const file of excess) {
      errors.push({
        file,
        reason: 'count',
        message: `Maximum ${totalAllowed} file${totalAllowed === 1 ? '' : 's'} allowed`,
      });
    }
  }

  return { valid, errors };
};

type FileUploadProps = React.ComponentProps<'div'> & {
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
  maxFiles?: number;
  files: FileUploadFile[];
  onFilesChange: (files: FileUploadFile[]) => void;
  onValidationError?: (errors: FileValidationError[]) => void;
  disabled?: boolean;
};

// gloss
const FileUpload = ({
  accept,
  multiple,
  maxSize,
  maxFiles,
  files,
  onFilesChange,
  onValidationError,
  disabled,
  className,
  children,
  ...props
}: FileUploadProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const openFilePicker = React.useCallback(() => {
    if (disabled) return;
    inputRef.current?.click();
  }, [disabled]);

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const incoming = Array.from(e.target.files ?? []);
      if (!incoming.length) return;

      const { valid, errors } = validateFiles(incoming, multiple ? files : [], { accept, maxSize, maxFiles, multiple });

      if (errors.length) onValidationError?.(errors);

      if (valid.length) {
        const newFiles: FileUploadFile[] = valid.map((file) => ({
          id: crypto.randomUUID(),
          file,
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        }));

        if (multiple) {
          onFilesChange([...files, ...newFiles]);
        } else {
          onFilesChange(newFiles.slice(0, 1));
        }
      }

      e.target.value = '';
    },
    [files, accept, maxSize, maxFiles, multiple, onFilesChange, onValidationError],
  );

  const ctxValue = React.useMemo<FileUploadContextValue>(
    () => ({
      files,
      onFilesChange,
      accept,
      multiple,
      maxSize,
      maxFiles,
      disabled,
      openFilePicker,
      onValidationError,
    }),
    [files, onFilesChange, accept, multiple, maxSize, maxFiles, disabled, openFilePicker, onValidationError],
  );

  return (
    <FileUploadContext.Provider value={ctxValue}>
      <div
        data-slot="file-upload"
        data-disabled={disabled || undefined}
        className={cn('grid gap-3', className)}
        {...props}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={handleInputChange}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        />
        {children}
      </div>
    </FileUploadContext.Provider>
  );
};

export type { FileItemContextValue, FileUploadContextValue, FileUploadFile, FileUploadProps, FileValidationError };
export { FileItemContext, FileUpload, FileUploadContext, useFileItemContext, useFileUploadContext, validateFiles };
